<template>
  <div class="nebChart">
    <div :id="chartId"
      :style="containerStyle"
    ></div>
  </div>
</template>

<script>
import embed from 'vega-embed'
import { expressionFunction } from 'vega'
import { guid } from '../../helpers'
import { isNaN as lisNaN } from 'lodash'

expressionFunction('suffix', function(datum, params) {
  return `${datum} ${params}`
})

export default {
  name: 'NebChart',

  props: {
    chartData: {
      type: Array,
      default: () => []
    },
    width: {
      type: String,
      default: '200px'
    },
    height: {
      type: String,
      default: '200px'
    },
    selectionMin: {
      type: Number,
      default: 0
    },
    selectionMax: {
      type: Number,
      default: 0
    }
  },

  data: () => ({
    chartId: null,
    vegaView: null,
    dirty: false
  }),

  computed: {
    computedConfig () {
      return {
        $schema: 'https://vega.github.io/schema/vega-lite/v4.json',
        width: 'container',
        height: 'container',
        config: {
          customFormatTypes: true,
          view: {
            stroke: 'transparent'
          },
          style: {
            assetCount: {
              color: '#9b9b9b',
              size: 12
            }
          },
          font: 'Helvetica Neue'
        },
        data: {
          values: this.chartData
        },
        layer: [
          {
            mark: 'bar',
            selection: {
              brush: {
                type: 'interval',
                encodings: ['x'],
                mark: {
                  cursor: 'grab'
                }
              }
            },
            encoding: {
              x: {
                field: 'left',
                bin: 'binned',
                title: null,
                axis: {
                  labels: false,
                  ticks: false,
                  grid: false,
                  domainColor: '#9b9b9b'
                },
                scale: {
                  domainMin: 0
                }
              },
              x2: {
                field: 'right'
              },
              y: {
                field: 'value',
                type: 'quantitative',
                title: null,
                scale: {type: 'sqrt'},
                axis: null
              },
              color: {
                value: '#74cfe7'
              },
              opacity: {
                condition: {
                  selection: 'brush',
                  value: 1
                },
                value: 0.4
              }
            }
          },
          {
            transform: [{
              filter: {selection: 'brush'}
            }],
            mark: {
              type: 'text',
              yOffset: 'height / 2 + 20',
              style: 'assetCount'
            },
            encoding: {
              text: {
                aggregate: 'sum',
                field: 'value',
                type: 'quantitative',
                format: 'Assets',
                formatType: 'suffix'
              },
              tooltip: {
                value: 'Asset Count'
              }
            }
          }
        ]
      }
    },
    containerStyle () {
      const { width, height } = this.$props
      return {
        width,
        height
      }
    },
    selectionData () {
      if (this.vegaView === null) { return [0, 0] }

      return this.vegaView.signal('brush_left')
    }
  },

  watch: {
    chartData () { this.doEmbed() },
    width () { this.$nextTick(() => { window.dispatchEvent(new Event('resize')) }) },
    height () { this.$nextTick(() => { window.dispatchEvent(new Event('resize')) }) },
    selectionData () {
      if (this.dirty) {
        this.dirty = false
        return
      }

      this.$emit('select', this.selectionData)
    },
    selectionMin (newValue) {
      if (lisNaN(newValue)) { return }
      this.dirty = true
      const range = [ Number(newValue), Number(this.selectionMax)]
      this.vegaView.signal('brush_x', range).run()
    },
    selectionMax (newValue) {
      if (lisNaN(newValue)) { return }
      this.dirty = true
      const range = [ Number(this.selectionMin), Number(newValue)]
      this.vegaView.signal('brush_x', range).run()
    }
  },

  methods: {
    setId () {
      return new Promise((resolve, reject) => {
        try  {
          const id = `nebChart-${String(guid()).replace(/-/g, '')}`
          this.chartId = id
          resolve(id)
        } catch (err) { reject(err) }
      })
    },
    doEmbed () {
      return embed(`#${this.chartId}`, this.computedConfig, { renderer: 'svg', actions: false })
        .then(result => { this.vegaView = result.view })
        .finally(() => { this.$emit('ready') })
    }
  },

  mounted () {
    this.setId()
      .then(() => this.doEmbed())
  }
}
</script>

<style lang="scss">
</style>
